import { Box, Typography } from '@mui/material'
import { FONT, MOVE_TINT } from './board'
import { formatDecimalPrice } from '../utils/price'
import { marginOf } from '../utils/margin'

/**
 * One book's markets, drawn as the snapshot draws them: a column per market, and inside it the line
 * beside the price it buys. The favourites board's cards are made of these.
 */

export const usable = (price) => typeof price === 'number' && price > 1

const ladderSx = {
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  justifyContent: 'start',
  columnGap: '8px',
  rowGap: '1px',
  fontSize: '13px',
  lineHeight: '19px',
  fontVariantNumeric: 'tabular-nums'
}

const markSx = { color: '#9A9EA3', fontSize: '11px', fontFamily: FONT }
const headSx = { ...markSx, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }
const labelSx = { color: '#9A9EA3', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

/**
 * Which line is the main one: the balanced one. A book moves the handicap until the two sides cost about
 * the same, so the pair closest to level is the line it is really trading, and the rungs above and below
 * are the ones nearest to it by the same measure.
 */
export const balanceOf = (line) => {
  const prices = (line.legs || []).map((leg) => leg.price).filter(usable)
  return prices.length < 2 ? Number.MAX_SAFE_INTEGER : Math.abs(prices[0] - prices[1])
}

export const mainLines = (lines, count) => [...(lines || [])]
  .sort((a, b) => balanceOf(a) - balanceOf(b))
  .slice(0, count)
  .sort((a, b) => (a.points === null ? 0 : a.points) - (b.points === null ? 0 : b.points))

/**
 * A market the book does not price is left out rather than drawn as a column of dashes. Fights and
 * outrights have no handicap and no total, and team totals belong to a handful of sports — empty columns
 * on every other card are exactly the width these boards exist to save.
 *
 * A column can hold more than one ladder: the two team totals are one market between them, and they sit
 * under a shared heading with each team named above its own prices.
 */
const Column = ({ head, parts, usPrices, dim, onPrice, movedOf }) => {
  const filled = parts.filter((part) => part.lines.length)
  if (!filled.length) {
    return null
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...headSx, marginBottom: '2px' }}>{head}</Typography>
      {filled.map((part) => (
        <Box key={part.market}>
          {part.title && (
            <Typography
              title={part.title}
              sx={{ ...markSx, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}
            >
              {part.title}
            </Typography>
          )}
          <Box sx={ladderSx}>
            {part.lines.flatMap((line) => line.legs.flatMap((leg) => ([
              <Box key={`${leg.runnerKey}|label`} sx={labelSx}>{leg.label}</Box>,
              <Box
                key={`${leg.runnerKey}|price`}
                title="Price history"
                onClick={() => onPrice && onPrice(part.market, line, leg)}
                sx={{
                  fontWeight: 700,
                  color: dim ? '#9A9EA3' : 'inherit',
                  cursor: onPrice ? 'pointer' : 'default',
                  // the tint is on a span in a grid rather than a table cell, so it is padded back out
                  // to look like a block behind the number rather than a stripe under it
                  backgroundColor: movedOf && movedOf(leg.runnerKey) ? MOVE_TINT[movedOf(leg.runnerKey)] : 'transparent',
                  borderRadius: '2px',
                  padding: '0 3px',
                  margin: '0 -3px',
                  transition: 'background-color 0.4s',
                  '&:hover': { textDecoration: onPrice ? 'underline' : 'none' }
                }}
              >
                {formatDecimalPrice(leg.price, usPrices)}
              </Box>
            ])))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

/**
 * A clicked price, described the way the history window wants it: which runner it is, which market it
 * belongs to, and what the whole market costs, so the window can show the overround beside it.
 */
export const historyRequestFor = (card, market, line, leg, book) => ({
  eventKey: `${card.home}|${card.away}|${card.koTime}`,
  home: card.home,
  away: card.away,
  koTime: card.koTime,
  // these boards only ever show the whole match; halves and quarters live on the event screen
  period: 'GAME',
  bookmaker: book,
  runnerKey: leg.runnerKey,
  price: leg.price,
  label: leg.selection,
  market,
  selection: leg.selection,
  margin: marginOf(line.legs.map((one) => one.price).filter(usable))
})

function Ladders({ game, markets, lines, usPrices, dim, onPrice, movedOf }) {
  const wants = (key) => markets.includes(key)
  const cells = [
    wants('ml') && <Column key="ml" head="1X2" usPrices={usPrices} dim={dim} onPrice={onPrice} movedOf={movedOf}
      parts={[{ market: 'Moneyline', lines: game.moneyline ? [game.moneyline] : [] }]} />,
    wants('spread') && <Column key="spread" head="AH" usPrices={usPrices} dim={dim} onPrice={onPrice} movedOf={movedOf}
      parts={[{ market: 'Spread', lines: mainLines(game.spreads, lines) }]} />,
    wants('total') && <Column key="total" head="AOU" usPrices={usPrices} dim={dim} onPrice={onPrice} movedOf={movedOf}
      parts={[{ market: 'Total', lines: mainLines(game.totals, lines) }]} />,
    wants('tt') && <Column key="tt" head="Team totals" usPrices={usPrices} dim={dim} onPrice={onPrice} movedOf={movedOf}
      parts={[
        { market: `${game.homeTeamName} total`, title: game.homeTeamName, lines: mainLines(game.homeTeamTotals, lines) },
        { market: `${game.awayTeamName} total`, title: game.awayTeamName, lines: mainLines(game.awayTeamTotals, lines) }
      ]} />
  ].filter(Boolean).filter((cell) => cell.props.parts.some((part) => part.lines.length))

  if (!cells.length) {
    return <Typography sx={{ ...markSx, padding: '4px 0' }}>Nothing to show for these markets</Typography>
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      {cells.map((cell, index) => (
        <Box
          key={index}
          sx={{
            minWidth: 0,
            paddingLeft: index === 0 ? 0 : '10px',
            borderLeft: index === 0 ? 'none' : '1px solid #EEEEF1'
          }}
        >
          {cell}
        </Box>
      ))}
    </Box>
  )
}

export default Ladders
